import React, { useState } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Shield, Phone, Mail, User, Download, ChevronLeft, ChevronRight } from 'lucide-react';

export const LeadFormSection = ({ onSubmit, isSubmitting = false, variant = 'full', sectionId = 'apply-form' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Video carousel data
  const videos = [
    {
      id: 'HMFJz2Dm0dc',
      url: 'https://www.youtube.com/embed/HMFJz2Dm0dc',
      title: 'Franchise Opportunity Tips & Advice'
    },
    {
      id: 'qtZV1MBmu5Y',
      url: 'https://www.youtube.com/embed/qtZV1MBmu5Y',
      title: 'Franchise Opportunity Tips & Advice'
    },
    {
      id: 'ayW25NlNm_8',
      url: 'https://www.youtube.com/embed/ayW25NlNm_8',
      title: 'Franchise Opportunity Tips & Advice'
    },
    {
      id: 'XdAysVxlR70',
      url: 'https://www.youtube.com/embed/XdAysVxlR70',
      title: 'Franchise Opportunity Tips & Advice'
    }
  ];

  // Simple carousel state
  const [currentVideo, setCurrentVideo] = useState(0);

  const nextVideo = () => {
    setCurrentVideo((prev) => (prev + 1) % videos.length);
  };

  const prevVideo = () => {
    setCurrentVideo((prev) => (prev - 1 + videos.length) % videos.length);
  };

  const handleSubmit = async (e, requestType = 'application') => {
    e.preventDefault();
    
    // Basic client-side validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      return;
    }
    
    // Add request type to form data
    const submissionData = {
      ...formData,
      requestType: requestType
    };
    
    await onSubmit(submissionData);
    
    // Reset form on successful submission
    if (!isSubmitting) {
      setFormData({ name: '', email: '', phone: '' });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Compact variant - just the form without video carousel
  if (variant === 'compact') {
    return (
      <section id={sectionId} data-testid={`${sectionId}-section`} className="py-16 bg-dark">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Start Your Journey Today
              </h2>
              <p className="text-lg text-gray-300">
                Get your franchise information pack or apply for your franchise opportunity
              </p>
            </div>
            
            <Card className="form-card bg-white shadow-2xl border-0">
              <CardContent className="px-6 py-6">
                <form onSubmit={(e) => handleSubmit(e, 'application')} className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="form-group">
                      <label className="block text-sm font-semibold text-dark mb-2">
                        <User className="w-4 h-4 inline mr-1" />
                        Full Name
                      </label>
                      <Input
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                        disabled={isSubmitting}
                        data-testid={`${sectionId}-name-input`}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-accent-primary focus:ring-0 transition-colors duration-200 disabled:opacity-50"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="block text-sm font-semibold text-dark mb-2">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email
                      </label>
                      <Input
                        type="email"
                        placeholder="Your email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        disabled={isSubmitting}
                        data-testid={`${sectionId}-email-input`}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-accent-primary focus:ring-0 transition-colors duration-200 disabled:opacity-50"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="block text-sm font-semibold text-dark mb-2">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Phone
                      </label>
                      <Input
                        type="tel"
                        placeholder="Your phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        required
                        disabled={isSubmitting}
                        data-testid={`${sectionId}-phone-input`}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-accent-primary focus:ring-0 transition-colors duration-200 disabled:opacity-50"
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid={`${sectionId}-apply-btn`}
                      className="flex-1 bg-accent-primary hover:bg-accent-primary/90 py-3 rounded-lg text-base font-semibold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ color: 'white' }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Apply Now'}
                    </Button>
                    
                    <Button
                      type="button"
                      onClick={(e) => handleSubmit(e, 'brochure')}
                      disabled={isSubmitting}
                      variant="outline"
                      data-testid={`${sectionId}-brochure-btn`}
                      className="flex-1 bg-white border-2 border-accent-primary text-accent-primary hover:bg-accent-primary hover:text-white py-3 rounded-lg text-base font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {isSubmitting ? 'Submitting...' : 'Get Free Brochure'}
                    </Button>
                  </div>
                </form>
                
                <div className="mt-4 text-center">
                  <div className="flex items-center justify-center text-subtle-light text-xs">
                    <Shield className="w-3 h-3 mr-1" />
                    Your details are safe with us
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  // Full variant with video carousel
  return (
    <section id={sectionId} data-testid={`${sectionId}-section`} className="py-24 bg-dark">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="section-title text-5xl md:text-6xl font-bold mb-6 text-white">
              Start Your Journey Today
            </h2>
            <p className="text-xl text-gray-300">
              Get your franchise information pack or apply for your franchise opportunity
            </p>
          </div>
          
          <Card className="form-card bg-white shadow-2xl border-0">
            <CardHeader className="pb-8">
              <CardTitle className="text-2xl font-bold text-center text-dark">
                Get Your Franchise Information
              </CardTitle>
              <p className="text-center text-subtle mt-2">
                Fill out your details below to receive your franchise brochure or submit your application
              </p>
            </CardHeader>
            
            <CardContent className="px-8 pb-8">
              <form onSubmit={(e) => handleSubmit(e, 'application')} className="space-y-6">
                <div className="form-group">
                  <label className="block text-sm font-semibold text-dark mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Full Name
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-accent-primary focus:ring-0 transition-colors duration-200 disabled:opacity-50"
                  />
                </div>
                
                <div className="form-group">
                  <label className="block text-sm font-semibold text-dark mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address
                  </label>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-accent-primary focus:ring-0 transition-colors duration-200 disabled:opacity-50"
                  />
                </div>
                
                <div className="form-group">
                  <label className="block text-sm font-semibold text-dark mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-accent-primary focus:ring-0 transition-colors duration-200 disabled:opacity-50"
                  />
                </div>
                
                {/* Form Footer - Backup Notice (moved to footer position before submit buttons) */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-4 mb-4">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <span>💬</span>
                    <span className="text-gray-600">If form does not work, click on "Have a Chat" before submitting</span>
                  </div>
                </div>
                
                {/* Two submission buttons */}
                <div className="space-y-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-white border-2 border-accent-primary text-accent-primary hover:bg-accent-primary hover:text-white py-4 rounded-xl text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Apply for Franchise Opportunity'}
                  </Button>
                  
                  <Button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'brochure')}
                    disabled={isSubmitting}
                    variant="outline"
                    className="w-full bg-white border-2 border-accent-primary text-accent-primary hover:bg-accent-primary hover:text-white py-4 rounded-xl text-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    {isSubmitting ? 'Submitting...' : 'Get Free Franchise Brochure (Instant Download)'}
                  </Button>
                </div>
              </form>
              
              <div className="privacy-notice mt-6 text-center">
                <div className="flex items-center justify-center text-subtle-light text-sm">
                  <Shield className="w-4 h-4 mr-2" />
                  We respect your privacy. Your details are safe with us.
                </div>
                <p className="text-xs text-subtle-light mt-2">
                  By submitting this form, you agree to our <a href="/privacy" target="_blank" className="text-accent-primary hover:underline">Privacy Policy</a> and 
                  consent to us contacting you about franchise opportunities. You can unsubscribe at any time.
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* YouTube Video Carousel */}
          <div className="mt-16">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-white mb-4">
                Why now is the right time
              </h3>
              <div className="text-gray-300 text-lg max-w-4xl mx-auto">
                <p className="mb-4">
                  The UK hair and beauty industry is worth over £3.5 billion annually, but not all salons are created equal. With more than 15 million people registered with the NHS for hair loss, the demand for specialist hair loss and extensions services is exploding.
                </p>
                <p>
                  At Need a Hair Makeover®, we've combined over 25 years of expertise, award-winning services, and a proven business model to create a franchise opportunity that stands out in 2026.
                </p>
              </div>
            </div>
            
            <div className="relative max-w-4xl mx-auto">
              {/* Current Video Display */}
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border-4 border-accent-primary/20">
                <iframe
                  src={videos[currentVideo].url}
                  title={videos[currentVideo].title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              
              {/* Navigation Buttons */}
              <button
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-accent-primary p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                onClick={prevVideo}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <button
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-accent-primary p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                onClick={nextVideo}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              
              {/* Dot Indicators */}
              <div className="flex justify-center mt-6 space-x-2">
                {videos.map((_, index) => (
                  <button
                    key={index}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentVideo ? 'bg-white' : 'bg-white/50 hover:bg-white/70'
                    }`}
                    onClick={() => setCurrentVideo(index)}
                  />
                ))}
              </div>
              
              {/* Video Title */}
              <div className="text-center mt-4">
                <h4 className="text-white font-semibold text-lg">
                  {videos[currentVideo].title}
                </h4>
                <p className="text-gray-300 text-sm mt-1">
                  Video {currentVideo + 1} of {videos.length}
                </p>
              </div>
            </div>
            
            {/* Post-Carousel Content */}
            <div className="mt-16 text-center max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  The hair and beauty industry is changing, and specialist salons are leading the way. By investing in a Need a Hair Makeover® franchise, you're stepping into a business that's:
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-2xl mb-2">✅</div>
                    <h4 className="text-white font-semibold text-lg">In high demand</h4>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl mb-2">✅</div>
                    <h4 className="text-white font-semibold text-lg">Backed by a proven system</h4>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl mb-2">✅</div>
                    <h4 className="text-white font-semibold text-lg">Designed for growth</h4>
                  </div>
                </div>
                
                <p className="text-gray-300 text-lg mb-8">
                  If you've ever dreamed of owning a profitable salon business with purpose, this is your chance.
                </p>
                
                {/* Call to Action */}
                <div className="bg-accent-primary/20 rounded-2xl p-6 border-2 border-accent-primary/30">
                  <h4 className="text-white text-2xl font-bold mb-4">Call to Action</h4>
                  <div className="flex items-center justify-center text-white text-lg">
                    <span className="mr-3">📩</span>
                    <span className="font-semibold">Request your franchise brochure today</span>
                  </div>
                  
                  <Button
                    onClick={() => {
                      const formElement = document.getElementById('apply-form');
                      if (formElement) {
                        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="mt-4 bg-white border-2 border-white text-accent-primary hover:bg-accent-primary hover:text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    Get Your Brochure Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};